import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import QrScanner from 'qr-scanner';
import { X } from 'lucide-react';
import { useAppDispatch } from '../store/hooks';
import { closeScanner } from '../store/slices/navigationSlice';
import { ExhibitId } from '../store/slices/exhibitSlice';
import { useOrientation } from '../hooks/useDeviceDetection';

interface QrScannerModalProps {
    isOpen: boolean;
    onClose: () => void;
    showCloseButton?: boolean;
}

export default function QrScannerModal({ isOpen, onClose, showCloseButton = true }: QrScannerModalProps) {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [scanner, setScanner] = useState<QrScanner | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [scanning, setScanning] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const orientation = useOrientation();

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    // Auto-close modal when device is rotated to landscape
    useEffect(() => {
        if (orientation === 'landscape' && isOpen) {
            console.log('QR Scanner: Auto-closing due to landscape orientation');
            onClose();
        }
    }, [orientation, isOpen, onClose]);

    // Handle modal visibility with animation timing
    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            // Start the entrance animation after a small delay
            setTimeout(() => setIsVisible(true), 10);
        } else {
            setIsVisible(false);
            // Hide the modal after animation completes
            const timer = setTimeout(() => {
                setShouldRender(false);
            }, 300); // Match the transition duration
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    const handleAnimatedClose = useCallback(() => {
        setIsVisible(false);
        // Wait for animation to complete before calling onClose
        setTimeout(() => {
            onClose();
        }, 300); // Match the transition duration
    }, [onClose]);
    
    const handleScan = (data: string | null) => {
        if (data) {
            // Close the modal immediately
            onClose();
            
            try {
                let url: URL;
                
                // Try to parse as URL first
                try {
                    url = new URL(data);
                } catch {
                    // Not a valid URL, try other formats...
                    
                    // Check if it's a direct text match for exhibit IDs
                    const textToExhibitMap: { [key: string]: string } = {
                        'book-of-myths': 'book-of-myths',
                        'badang': 'legend-of-badang',
                        'legend-of-badang': 'legend-of-badang',
                        'orang-laut': 'orang-laut',
                        'early-market-traders': 'early-market-traders',
                        'market-traders': 'early-market-traders',
                        'peranakan-culture': 'peranakan-culture',
                        'peranakan-design': 'peranakan-design',
                        'peranakan-patterns': 'peranakan-patterns',
                        'sg60': 'sg60',
                        'singapore-60': 'sg60'
                    };
                    
                    const matchedExhibit = textToExhibitMap[data.toLowerCase().trim()];
                    if (matchedExhibit) {
                        // Navigate directly to the matched exhibit
                        navigate(`/exhibit/${matchedExhibit}`);
                        return;
                    }
                    
                    // If no direct match, show error
                    setError('Invalid QR code format');
                    setTimeout(() => setError(''), 3000);
                    return;
                }
                
                // Extract exhibit ID from URL path
                const pathParts = url.pathname.split('/');
                const exhibitIndex = pathParts.findIndex(part => part === 'exhibit');
                const exhibitId = exhibitIndex !== -1 && exhibitIndex + 1 < pathParts.length 
                    ? pathParts[exhibitIndex + 1] 
                    : null;
                
                if (!exhibitId) {
                    setError('Invalid QR code format');
                    setTimeout(() => setError(''), 3000);
                    return;
                }
                
                // List of zone-based exhibits that need special QR scan handling
                const zoneBasedExhibits = ['orang-laut', 'market-traders', 'early-market-traders', 'peranakan-design', 'peranakan-patterns'];
                
                if (zoneBasedExhibits.includes(exhibitId)) {
                    // Create QR scan parameters
                    const qrScanParams = new URLSearchParams({
                        fromQrScan: 'true',
                        qrTimestamp: Date.now().toString()
                    });
                    
                    // Special handling for peranakan exhibits - always navigate to design page first
                    if (exhibitId === 'peranakan-patterns' || exhibitId === 'peranakan-design') {
                        const targetUrl = `/exhibit/peranakan-design?${qrScanParams.toString()}`;
                        navigate(targetUrl);
                    } else if (exhibitId === 'early-market-traders') {
                        // Map early-market-traders to market-traders route with QR scan params
                        const targetUrl = `/exhibit/market-traders?${qrScanParams.toString()}`;
                        navigate(targetUrl);
                    } else {
                        // Other zone-based exhibits
                        const targetUrl = `/exhibit/${exhibitId}?${qrScanParams.toString()}`;
                        navigate(targetUrl);
                    }
                } else {
                    // Non-zone exhibits - navigate normally
                    const targetUrl = `/exhibit/${exhibitId}`;
                    navigate(targetUrl);
                }
                
            } catch (error) {
                console.error('Error processing QR code:', error);
                setError('Invalid QR code format');
                setTimeout(() => setError(''), 3000);
            }
        }
    };

    // Initialize scanner when modal opens
    useEffect(() => {
        if (isOpen && videoRef.current && !scanner) {
            try {
                // Initialize QR scanner
                const newScanner = new QrScanner(
                    videoRef.current,
                    result => {
                        handleScan(result.data);
                    },
                    {
                        returnDetailedScanResult: true,
                        highlightScanRegion: true,
                        highlightCodeOutline: true,
                        preferredCamera: 'environment',
                    }
                );
                
                setScanner(newScanner);
                newScanner.start().then(() => {
                    setScanning(true);
                }).catch(err => {
                    setError(t('qrScanner.cameraError'));
                    console.error(err);
                });
            } catch (err) {
                setError(t('qrScanner.initError'));
                console.error(err);
            }
        }
        
        // Cleanup scanner when modal closes
        if (!isOpen && scanner) {
            scanner.destroy();
            setScanner(null);
            setScanning(false);
            setError(null);
        }
    }, [isOpen, scanner, t, handleScan]);

    // Close modal on Escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                handleAnimatedClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }
        
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, handleAnimatedClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);
    
    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            handleAnimatedClose();
        }
    };

    if (!shouldRender) return null;

    return (
        <div 
            className={`
                fixed inset-0 z-50 bg-black transition-opacity duration-300 ease-in-out
                ${isVisible ? 'bg-opacity-75 opacity-100' : 'bg-opacity-0 opacity-0'}
                flex items-end sm:items-center justify-center p-4
            `}
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="qr-scanner-modal-title"
        >
            {/* Modal Content */}
            <div 
                className={`
                    relative bg-black/40 backdrop-blur-xl border border-rose-600/70 rounded-[36px] overflow-hidden shadow-2xl w-full max-w-lg mx-auto
                    transform transition-all duration-300 ease-out
                    ${isVisible 
                        ? 'translate-y-0 opacity-100 scale-100' 
                        : 'translate-y-full opacity-0 scale-95'
                    }
                    sm:translate-y-0
                    before:absolute before:inset-0 before:rounded-xl before:bg-gradient-to-br before:from-white/10 before:to-transparent before:pointer-events-none
                `}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header with close button */}
                <div className={`flex items-center p-4 border-b border-white/10 ${showCloseButton ? 'justify-between' : 'justify-center'} relative z-10`}>
                    <h2 id="qr-scanner-modal-title" className="text-xl font-semibold text-white">
                        {t('qrScanner.title')}
                    </h2>
                    {showCloseButton && (
                        <button
                            onClick={handleAnimatedClose}
                            className="text-white/70 hover:text-white transition-colors p-1"
                            aria-label={t('common.close')}
                        >
                            <X size={24} />
                        </button>
                    )}
                </div>

                {/* Scanner Content */}
                <div className="p-6 relative z-10">
                    {error && (
                        <div className="mb-4 p-3 bg-red-500/20 border border-red-400/50 text-red-200 rounded-xl backdrop-blur-sm break-all text-sm">
                            {error}
                        </div>
                    )}
                    
                    <div className="relative mx-auto mb-4">
                        <div className="w-[70vw] h-[70vw] max-w-full max-h-[512px] mx-auto overflow-hidden rounded-xl border-2 border-white/30 bg-black/50 backdrop-blur-sm shadow-inner">
                            <video 
                                ref={videoRef} 
                                className="w-full h-full object-cover rounded-xl overflow-hidden"
                                playsInline
                                muted
                            />
                        </div>
                        
                        {scanning && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="w-[60vw] h-[60vw] max-w-full max-h-full border-2 border-white/50 opacity-70 animate-pulse rounded-xl shadow-lg"></div>
                            </div>
                        )}
                    </div>
                    
                    <p className="text-center text-white/80 text-sm">
                        {t('qrScanner.instructions')}
                    </p>
                </div>

                {/* Footer */}
                <div className="p-4 text-center border-t border-white/10 bg-white/5 rounded-b-lg relative z-10">
                    <button 
                        onClick={handleAnimatedClose}
                        className={`${isVisible ? 'w-full' : 'w-1/4'} bg-rose-600/70 text-white py-3 px-6 rounded-full transition-all duration-500 backdrop-blur-sm uppercase`}
                    >
                        {t('common.close')}
                    </button>
                </div>
            </div>
        </div>
    );
} 